/* ==========================================================================
   Salon Order Builder (Covet & Mane) — front-end logic
   Product data comes from the section (data-sob-data), sourced from the
   custom.salon_products page metafield. Totals, discount tiers, progress bar,
   gift tiers and search are all computed client-side.
   ========================================================================== */
(function () {
  'use strict';

  function initRoot(root) {
    if (!root || root.dataset.sobReady === 'true') return;
    var dataEl = (root.parentNode || document).querySelector('[data-sob-data]');
    if (!dataEl) return;
    root.dataset.sobReady = 'true';

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var sym = data.currencySymbol || '$';
    var volumeTiers = (data.volumeTiers || []).slice().sort(function (a, b) { return a.min - b.min; });
    var giftTiers = (data.giftTiers || []).slice().sort(function (a, b) { return a.threshold - b.threshold; });

    // Map group id (product handle) -> group name, for matching intro-offer requirements against ordered items.
    var groupNames = {};
    (data.categories || []).forEach(function (cat) { (cat.groups || []).forEach(function (g) { groupNames[g.id] = g.name || ''; }); });

    // Does an ordered item satisfy a requirement matcher? `all` = every keyword present; `any` = at least one present.
    function itemMatches(itemText, req) {
      var t = itemText;
      if (req.all && req.all.length && !req.all.every(function (k) { return t.indexOf(String(k).toLowerCase()) !== -1; })) return false;
      if (req.any && req.any.length && !req.any.some(function (k) { return t.indexOf(String(k).toLowerCase()) !== -1; })) return false;
      return (req.all && req.all.length) || (req.any && req.any.length);
    }
    // Total $ of ordered items matching a requirement.
    function reqMetValue(req, items) {
      var sum = 0;
      items.forEach(function (it) { if (itemMatches(it.text, req)) sum += it.line; });
      return sum;
    }
    // A tier is unlocked when every one of its requirements is met.
    function tierUnlocked(tier, items) {
      if (!tier.requirements || !tier.requirements.length) return false;
      return tier.requirements.every(function (req) { return reqMetValue(req, items) >= (req.minValue || 0); });
    }

    // Collect the currently-ordered items as { text, line } for requirement matching (used by compute + export).
    function collectOrderedItems() {
      var items = [];
      (typeof qtyInputs !== 'undefined' ? qtyInputs : []).forEach(function (input) {
        var qty = parseInt(input.value, 10) || 0;
        if (qty <= 0) return;
        var price = parseFloat(input.getAttribute('data-price')) || 0;
        var gid = input.getAttribute('data-group');
        var row = input.closest('.sob__row');
        var skuEl = row.querySelector('.sob__row-sku');
        var text = ((groupNames[gid] || '') + ' ' + (row.getAttribute('data-shade') || '') + ' ' +
          (row.getAttribute('data-len') || '') + ' ' + (skuEl ? skuEl.textContent : '')).toLowerCase();
        items.push({ text: text, line: qty * price });
      });
      return items;
    }
    // Highest unlocked gift tier (by threshold order) for the current order, or null.
    function highestUnlockedTier(items) {
      var tier = null;
      giftTiers.forEach(function (g) { if (tierUnlocked(g, items)) tier = g; });
      return tier;
    }

    // Localised currency formatter driven by the active market currency/locale (falls back to symbol).
    var moneyFmt = null;
    try {
      if (data.currency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
        moneyFmt = new Intl.NumberFormat(data.locale || undefined, { style: 'currency', currency: data.currency, maximumFractionDigits: 0 });
      }
    } catch (e) { moneyFmt = null; }
    function money(n) {
      n = Math.round(n || 0);
      if (moneyFmt) { try { return moneyFmt.format(n); } catch (e) { /* fall through */ } }
      return sym + n.toLocaleString('en-US');
    }
    // Two-decimal variant for derived allowance values (e.g. 3% of total).
    var moneyFmt2 = null;
    try {
      if (data.currency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
        moneyFmt2 = new Intl.NumberFormat(data.locale || undefined, { style: 'currency', currency: data.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    } catch (e) { moneyFmt2 = null; }
    function money2(n) {
      n = n || 0;
      if (moneyFmt2) { try { return moneyFmt2.format(n); } catch (e) { /* fall through */ } }
      return sym + n.toFixed(2);
    }
    // Sort key for lengths like 10", 14", 18" — leading number; non-numeric lengths sort last.
    function lenSort(v) { var m = String(v == null ? '' : v).match(/[\d.]+/); return m ? parseFloat(m[0]) : Number.MAX_SAFE_INTEGER; }

    var els = {
      products: root.querySelector('[data-sob-products]'),
      breakdownWrap: root.querySelector('.sob__breakdown-wrap'),
      breakdown: root.querySelector('[data-sob-breakdown]'),
      subtotal: root.querySelector('[data-sob-subtotal]'),
      discountRow: root.querySelector('[data-sob-discount-row]'),
      discountTier: root.querySelector('[data-sob-discount-tier]'),
      discount: root.querySelector('[data-sob-discount]'),
      total: root.querySelector('[data-sob-total]'),
      giftValue: root.querySelector('[data-sob-gift-value]'),
      allowance: root.querySelector('[data-sob-allowance]'),
      exclusivity: root.querySelector('[data-sob-exclusivity]'),
      newCustomer: root.querySelector('[data-sob-newcustomer]'),
      offers: root.querySelector('[data-sob-offers]'),
      allowances: root.querySelector('[data-sob-allowances]'),
      progressLabel: root.querySelector('[data-sob-progress-label]'),
      progressFill: root.querySelector('[data-sob-progress-fill]'),
      intro: root.querySelector('[data-sob-intro]'),
      introLabel: root.querySelector('[data-sob-intro-label]'),
      introFill: root.querySelector('[data-sob-intro-fill]'),
      introReqs: root.querySelector('[data-sob-intro-reqs]'),
      gifts: root.querySelector('[data-sob-gifts]'),
      giftsTier: root.querySelector('[data-sob-gifts-tier]'),
      giftsList: root.querySelector('[data-sob-gifts-list]'),
      giftsTotal: root.querySelector('[data-sob-gifts-total]'),
      giftsNote: root.querySelector('[data-sob-gifts-note]'),
      vtiers: root.querySelector('[data-sob-vtiers]'),
      tiers: root.querySelectorAll('[data-sob-tiers] .sob__tier'),
      search: root.querySelector('[data-sob-search]'),
      clear: root.querySelector('[data-sob-clear]'),
      export: root.querySelector('[data-sob-export]'),
      addcart: root.querySelector('[data-sob-addcart]')
    };

    /* -------- Customer fields -------- */
    var fieldEls = {}, errorEls = {};
    root.querySelectorAll('[data-sob-field]').forEach(function (el) { fieldEls[el.getAttribute('data-sob-field')] = el; });
    root.querySelectorAll('[data-sob-error]').forEach(function (el) { errorEls[el.getAttribute('data-sob-error')] = el; });

    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var REQUIRED_FIELDS = {
      firstName: 'First name is required.',
      lastName: 'Last name is required.',
      email: 'Email is required.',
      company: 'Company / salon name is required.'
    };

    function isNewCustomer() { return !els.newCustomer || els.newCustomer.checked; }

    function getCustomer() {
      function v(k) { return fieldEls[k] ? fieldEls[k].value.trim() : ''; }
      return {
        firstName: v('firstName'), lastName: v('lastName'), email: v('email'),
        company: v('company'), address: v('address'), newCustomer: isNewCustomer()
      };
    }

    function setFieldError(key, msg) {
      var input = fieldEls[key]; if (!input) return;
      var field = input.closest('.sob__field');
      if (field) field.classList.toggle('is-invalid', !!msg);
      if (errorEls[key]) errorEls[key].textContent = msg || '';
    }

    function validateCustomer() {
      var ok = true, firstInvalid = null;
      Object.keys(REQUIRED_FIELDS).forEach(function (k) {
        var val = fieldEls[k] ? fieldEls[k].value.trim() : '';
        if (!val) { setFieldError(k, REQUIRED_FIELDS[k]); ok = false; if (!firstInvalid) firstInvalid = fieldEls[k]; }
        else setFieldError(k, '');
      });
      var email = fieldEls.email ? fieldEls.email.value.trim() : '';
      if (email && !EMAIL_RE.test(email)) { setFieldError('email', 'Enter a valid email address.'); ok = false; if (!firstInvalid) firstInvalid = fieldEls.email; }
      if (firstInvalid && firstInvalid.focus) firstInvalid.focus();
      return ok;
    }

    // Intro offers + gift/allowance logic apply only for new customers.
    function applyNewCustomer() {
      var nc = isNewCustomer();
      if (els.offers) els.offers.classList.toggle('is-open', nc);
      if (els.allowances) els.allowances.hidden = !nc;
      if (!nc && els.gifts) els.gifts.hidden = true;
      compute();
    }

    Object.keys(fieldEls).forEach(function (k) {
      fieldEls[k].addEventListener('input', function () { setFieldError(k, ''); });
    });
    if (els.newCustomer) els.newCustomer.addEventListener('change', applyNewCustomer);

    /* -------- Render products -------- */
    var productsHtml = '';
    (data.categories || []).forEach(function (cat) {
      (cat.groups || []).forEach(function (group) {
        var body = '';
        var curLen = null, curBand = null;
        // Group by length, then colour family (band), then colour name within each family.
        var rows = (group.rows || []).slice().sort(function (a, b) {
          var la = lenSort(a.length), lb = lenSort(b.length);
          if (la !== lb) return la - lb;
          var ba = a.bandOrder == null ? 999 : a.bandOrder, bb = b.bandOrder == null ? 999 : b.bandOrder;
          if (ba !== bb) return ba - bb;
          return String(a.shade).localeCompare(String(b.shade));
        });
        rows.forEach(function (r) {
          if (r.length !== curLen) { curLen = r.length; curBand = null; if (r.length) body += '<p class="sob__len">' + esc(r.length) + '</p>'; }
          if (r.band && r.band !== curBand) { curBand = r.band; body += '<p class="sob__band">' + esc(r.band) + '</p>'; }

          // Out of stock (unorderable) = not available. Backorder = tracked, sold out, but "continue selling when OOS".
          var oos = r.available === false;
          var backorder = r.tracked === true && r.available !== false && (r.inventory || 0) <= 0 && r.continueSelling === true;
          var bestseller = r.bestseller === true || r.bestseller === 'true';
          var badges = '';
          if (bestseller) {
            badges += '<span class="sob__badge sob__badge--best">Bestseller</span>';
          }
          if (oos) {
            badges += '<span class="sob__badge sob__badge--oos">Out of stock</span>';
          } else if (backorder) {
            badges += '<span class="sob__badge sob__badge--back">Backorder</span>';
          }
          // Cap qty to on-hand stock only when tracked and not allowed to oversell.
          var maxAttr = (r.tracked === true && r.continueSelling !== true && (r.inventory || 0) > 0) ? ' max="' + r.inventory + '"' : '';

          body +=
            '<div class="sob__row' + (oos ? ' sob__row--oos' : '') + '" data-terms="' + esc((r.shade + ' ' + r.sku + ' ' + r.length).toLowerCase()) + '" data-shade="' + esc(r.shade) + '" data-len="' + esc(r.length) + '">' +
              (r.image ? '<span class="sob__swatch"><img src="' + esc(r.image) + '" alt="" loading="lazy"></span>' : '<span class="sob__swatch" style="background:' + esc(r.swatch || '#ccc') + '"></span>') +
              '<span class="sob__row-name"><span class="sob__row-shade">' + esc(r.shade) + badges + '</span><span class="sob__row-sku">' + esc(r.sku) + '</span></span>' +
              '<span class="sob__row-price">' + money(r.price) + '</span>' +
              '<input type="number" min="0" step="1"' + maxAttr + ' class="sob__qty" value="0" data-group="' + esc(group.id) + '" data-price="' + r.price + '" data-variant-id="' + esc(r.variantId == null ? '' : r.variantId) + '"' + (oos ? ' disabled aria-disabled="true"' : '') + ' aria-label="Quantity for ' + esc(r.shade + ' ' + r.length) + '">' +
              '<span class="sob__line" data-line>' + money(0) + '</span>' +
            '</div>';
        });

        productsHtml +=
          '<section class="sob__group" data-group-block="' + esc(group.id) + '">' +
            '<button type="button" class="sob__group-head" aria-expanded="false">' +
              '<span><span class="sob__group-title">' + esc(group.name) + '</span><span class="sob__group-skus">(' + (group.skus || (group.rows || []).length) + ' SKUs)</span></span>' +
              '<span class="sob__group-meta"><span class="sob__group-count" data-group-count="' + esc(group.id) + '"></span>' +
              '<svg class="sob__group-caret" width="14" height="14" viewBox="0 0 20 20" aria-hidden="true"><path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
            '</button>' +
            '<div class="sob__group-body">' + body + '</div>' +
          '</section>';
      });
    });
    els.products.innerHTML = productsHtml || '<p class="sob__empty">No products configured yet. Add products to the <code>custom.salon_products</code> page metafield.</p>';

    /* -------- Render volume tiers table -------- */
    els.vtiers.innerHTML = volumeTiers.map(function (t) {
      return '<div class="sob__vtier" data-vtier-min="' + t.min + '"><span>' + money(t.min) + '+</span><span>' + t.pct + '%</span></div>';
    }).join('');

    /* -------- Edge fades: show when the Your Order breakdown is clipped top/bottom -------- */
    function updateClip() {
      if (!els.breakdownWrap || !els.breakdown) return;
      var el = els.breakdown;
      var scrollable = el.scrollHeight - el.clientHeight > 2;
      els.breakdownWrap.classList.toggle('is-clip-top', scrollable && el.scrollTop > 2);
      els.breakdownWrap.classList.toggle('is-clip-bottom', scrollable && (el.scrollTop + el.clientHeight) < (el.scrollHeight - 2));
    }
    if (els.breakdown) els.breakdown.addEventListener('scroll', updateClip, { passive: true });
    window.addEventListener('resize', updateClip);

    /* -------- Accordion -------- */
    els.products.querySelectorAll('.sob__group-head').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var g = btn.closest('.sob__group');
        var open = g.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    /* -------- "Your Investment" quick links: open the matching product + scroll to it -------- */
    function goto(spec) {
      if (!spec) return;
      if (spec === 'top') {
        if (els.products) els.products.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      var kws = spec.split('|').map(function (k) { return k.trim().toLowerCase(); }).filter(Boolean);
      var rows = els.products.querySelectorAll('.sob__row');
      var match = null;
      for (var i = 0; i < rows.length; i++) {
        var g = rows[i].closest('.sob__group');
        var titleEl = g ? g.querySelector('.sob__group-title') : null;
        var text = ((titleEl ? titleEl.textContent : '') + ' ' + (rows[i].getAttribute('data-terms') || '')).toLowerCase();
        if (kws.every(function (k) { return text.indexOf(k) !== -1; })) { match = rows[i]; break; }
      }
      if (!match) { if (els.products) els.products.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
      var group = match.closest('.sob__group');
      if (group && !group.classList.contains('is-open')) {
        group.classList.add('is-open');
        var head = group.querySelector('.sob__group-head');
        if (head) head.setAttribute('aria-expanded', 'true');
      }
      requestAnimationFrame(function () {
        match.scrollIntoView({ behavior: 'smooth', block: 'center' });
        match.classList.remove('is-flash');
        void match.offsetWidth; // restart the flash animation
        match.classList.add('is-flash');
        setTimeout(function () { match.classList.remove('is-flash'); }, 1800);
      });
    }
    root.querySelectorAll('[data-sob-goto]').forEach(function (btn) {
      btn.addEventListener('click', function () { goto(btn.getAttribute('data-sob-goto')); });
    });

    /* -------- Compute -------- */
    var qtyInputs = Array.prototype.slice.call(els.products.querySelectorAll('.sob__qty'));
    var groupTotals = {};
    var groupUnits = {};

    /* -------- Render breakdown -------- */
    function renderBreakdown() {
      var selectedCount = qtyInputs.filter(function (input) {
        return (parseInt(input.value, 10) || 0) > 0;
      }).length;

      if (!selectedCount) {
        els.breakdown.innerHTML = '<p class="sob__empty">Add items to see your order breakdown.</p>';
        return;
      }

      var html = '';
      (data.categories || []).forEach(function (cat) {
        (cat.groups || []).forEach(function (group) {
          var gid = group.id;
          var groupTotal = groupTotals[gid] || 0;
          if (groupTotal <= 0) return;

          html += '<p class="sob__bd-cat">' + esc(group.summaryLabel || group.name) + '</p>';

          qtyInputs.forEach(function (input) {
            if (input.getAttribute('data-group') !== gid) return;
            var qty = parseInt(input.value, 10) || 0;
            if (qty <= 0) return;

            var row = input.closest('.sob__row');
            var price = parseFloat(input.getAttribute('data-price')) || 0;
            var lineTotal = qty * price;
            var shade = (row.getAttribute('data-shade') || '').trim();
            var len = (row.getAttribute('data-len') || '').trim();
            var variantLabel = (shade + (len ? ' ' + len : '')).trim() || shade;

            html +=
              '<div class="sob__bd-row" data-bd-group="' + esc(gid) + '">' +
                '<span>' + esc(variantLabel) + ' x ' + qty + '</span>' +
                '<span data-bd-value="">' + money(lineTotal) + '</span>' +
              '</div>';
          });
        });
      });

      els.breakdown.innerHTML = html || '<p class="sob__empty">Add items to see your order breakdown.</p>';
    }

    function compute() {
      groupTotals = {};
      groupUnits = {};
      var subtotal = 0;
      var orderedItems = [];

      qtyInputs.forEach(function (input) {
        var qty = parseInt(input.value, 10) || 0;
        var price = parseFloat(input.getAttribute('data-price')) || 0;
        var gid = input.getAttribute('data-group');
        var line = qty * price;
        var row = input.closest('.sob__row');
        row.querySelector('[data-line]').textContent = money(line);
        row.classList.toggle('has-qty', qty > 0);
        groupTotals[gid] = (groupTotals[gid] || 0) + line;
        groupUnits[gid] = (groupUnits[gid] || 0) + qty;
        subtotal += line;
        if (qty > 0) {
          var skuEl = row.querySelector('.sob__row-sku');
          var text = ((groupNames[gid] || '') + ' ' + (row.getAttribute('data-shade') || '') + ' ' +
            (row.getAttribute('data-len') || '') + ' ' + (skuEl ? skuEl.textContent : '')).toLowerCase();
          orderedItems.push({ text: text, line: line });
        }
      });

      // Render the Your Order breakdown with selected variants only
      renderBreakdown();
      updateClip();

      // Group count badges
      root.querySelectorAll('[data-group-count]').forEach(function (c) {
        var u = groupUnits[c.getAttribute('data-group-count')] || 0;
        c.textContent = u > 0 ? (u + ' added') : '';
      });

      // volume discount
      var current = null, next = null;
      volumeTiers.forEach(function (t) { if (subtotal >= t.min) current = t; });
      for (var i = 0; i < volumeTiers.length; i++) { if (volumeTiers[i].min > subtotal) { next = volumeTiers[i]; break; } }
      var pct = current ? current.pct : 0;
      var discount = Math.round(subtotal * pct / 100);
      var total = subtotal - discount;

      els.subtotal.textContent = money(subtotal);
      els.total.textContent = money(total);
      if (pct > 0) {
        els.discountRow.hidden = false;
        els.discountTier.textContent = pct + '% tier';
        els.discount.textContent = '−' + money(discount);
      } else {
        els.discountRow.hidden = true;
      }

      // progress bar
      if (next) {
        els.progressLabel.innerHTML = '<strong>' + money(next.min - subtotal) + ' more</strong> unlocks the ' + next.pct + '% discount tier.';
        els.progressFill.style.width = Math.min(100, (subtotal / next.min) * 100) + '%';
      } else if (volumeTiers.length) {
        els.progressLabel.textContent = 'Top ' + volumeTiers[volumeTiers.length - 1].pct + '% discount tier reached.';
        els.progressFill.style.width = '100%';
      }

      // Introductory offers (gift tiers, complimentary gifts, allowances) apply only for new customers.
      // Unlocking is condition-based: each tier requires specific "Your Investment" items (see requirements).
      if (isNewCustomer()) {
        // gift value (3% of order total) + exclusivity allowance
        els.giftValue.textContent = money(total * 0.03) + ' value';

        var unlocked = {}, highestUnlocked = null;
        giftTiers.forEach(function (g) {
          if (tierUnlocked(g, orderedItems)) { unlocked[g.threshold] = true; highestUnlocked = g; }
        });
        els.allowance.textContent = money(highestUnlocked ? (highestUnlocked.allowance || 0) : 0) + ' value';

        // Highlight every unlocked package, but the check icon marks only the applied (highest) one.
        els.tiers.forEach(function (card) {
          var th = parseFloat(card.getAttribute('data-tier-threshold')) || 0;
          card.classList.toggle('is-active', !!unlocked[th]);
          card.classList.toggle('is-applied', !!highestUnlocked && th === highestUnlocked.threshold);
        });

        // complimentary gifts (highest unlocked tier that defines a gift set)
        var giftSetTier = null;
        giftTiers.forEach(function (g) { if (unlocked[g.threshold] && g.gifts && g.gifts.length) giftSetTier = g; });
        if (giftSetTier) {
          els.gifts.hidden = false;
          els.giftsTier.textContent = 'Unlocked: ' + giftSetTier.name;
          var gv = 0;
          els.giftsList.innerHTML = giftSetTier.gifts.map(function (g) { gv += g.value || 0; return '<li><span>— ' + esc(g.label) + '</span><span>' + money(g.value) + '</span></li>'; }).join('');
          els.giftsTotal.textContent = money(gv) + ' value';

          var allowanceTxt = money2(total * 0.03) + ' in the volume gift allowance and any exclusivity allowance shown above.';
          var nextGiftTier = null;
          for (var gi = 0; gi < giftTiers.length; gi++) {
            if (giftTiers[gi].threshold > giftSetTier.threshold) { nextGiftTier = giftTiers[gi]; break; }
          }
          if (nextGiftTier) {
            var more = Math.max(0, nextGiftTier.threshold - subtotal);
            els.giftsNote.textContent = 'Complimentary tier applied, spend ' + money2(more) + ' more to unlock the next tier. This order also carries ' + allowanceTxt;
          } else {
            els.giftsNote.textContent = 'Highest complimentary tier reached. This order also carries ' + allowanceTxt;
          }
        } else {
          els.gifts.hidden = true;
        }

        renderIntroProgress(orderedItems);
        if (els.intro) els.intro.hidden = false;
      } else {
        if (els.gifts) els.gifts.hidden = true;
        if (els.intro) els.intro.hidden = true;
        els.tiers.forEach(function (card) { card.classList.remove('is-active'); card.classList.remove('is-applied'); });
      }

      // volume tiers table highlight
      root.querySelectorAll('[data-vtier-min]').forEach(function (r) {
        r.classList.toggle('is-active', current && parseFloat(r.getAttribute('data-vtier-min')) === current.min);
      });
    }

    // Second progress bar: progress toward the tier ABOVE the highest one already unlocked.
    // (Once a higher tier's amount threshold is met, lower tiers' specific-variant conditions
    //  no longer matter — the customer already qualifies for a better package.)
    function renderIntroProgress(items) {
      if (!els.intro) return;
      var highestIdx = -1;
      for (var i = 0; i < giftTiers.length; i++) { if (tierUnlocked(giftTiers[i], items)) highestIdx = i; }
      // Top tier already unlocked → nothing more to aim for.
      if (highestIdx === giftTiers.length - 1) {
        els.introLabel.innerHTML = '<strong>Highest introductory offer unlocked.</strong>';
        if (els.introFill) els.introFill.style.width = '100%';
        if (els.introReqs) els.introReqs.innerHTML = '';
        return;
      }
      var next = giftTiers[highestIdx + 1];
      if (!next) {
        els.introLabel.innerHTML = '<strong>Highest introductory offer unlocked.</strong>';
        if (els.introFill) els.introFill.style.width = '100%';
        if (els.introReqs) els.introReqs.innerHTML = '';
        return;
      }
      var got = 0, need = 0;
      var reqsHtml = (next.requirements || []).map(function (req) {
        var mv = reqMetValue(req, items);
        var capped = Math.min(mv, req.minValue || 0);
        got += capped; need += (req.minValue || 0);
        var met = mv >= (req.minValue || 0);
        return '<li class="' + (met ? 'is-met' : '') + '">' +
          '<span><span class="sob__req-mark">' + (met ? '✓' : '○') + '</span> ' + esc(req.label) + '</span>' +
          '<span>' + money(capped) + ' / ' + money(req.minValue || 0) + '</span>' +
        '</li>';
      }).join('');
      var pct = need > 0 ? Math.round(got / need * 100) : 0;
      els.introLabel.innerHTML = 'Add the items below to unlock <strong>' + esc(next.name) + '</strong>.';
      if (els.introFill) els.introFill.style.width = pct + '%';
      if (els.introReqs) els.introReqs.innerHTML = reqsHtml;
    }

    qtyInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        // Clamp to available stock when a max (on-hand, no oversell) is set.
        var max = parseInt(input.getAttribute('max'), 10);
        var qty = parseInt(input.value, 10);
        if (!isNaN(max) && !isNaN(qty) && qty > max) { input.value = max; }
        if (!isNaN(qty) && qty < 0) { input.value = 0; }
        compute();
      });
      input.addEventListener('focus', function () { input.select(); });
    });

    /* -------- Search filter -------- */
    if (els.search) {
      els.search.addEventListener('input', function () {
        var q = els.search.value.trim().toLowerCase();
        els.products.querySelectorAll('.sob__group').forEach(function (group) {
          var anyVisible = false;
          group.querySelectorAll('.sob__row').forEach(function (row) {
            var match = !q || row.getAttribute('data-terms').indexOf(q) !== -1;
            row.classList.toggle('is-hidden', !match);
            if (match) anyVisible = true;
          });
          // hide length/band headers that have no visible rows in their scope
          group.querySelectorAll('.sob__len, .sob__band').forEach(function (h) {
            var isLen = h.classList.contains('sob__len');
            var vis = false, n = h.nextElementSibling;
            while (n) {
              if (n.classList.contains('sob__len')) break;
              if (!isLen && n.classList.contains('sob__band')) break;
              if (n.classList.contains('sob__row') && !n.classList.contains('is-hidden')) { vis = true; break; }
              n = n.nextElementSibling;
            }
            h.style.display = vis ? '' : 'none';
          });
          group.style.display = (q && !anyVisible) ? 'none' : '';
          if (q && anyVisible) { group.classList.add('is-open'); group.querySelector('.sob__group-head').setAttribute('aria-expanded', 'true'); }
        });
      });
    }

    /* -------- Clear -------- */
    if (els.clear) {
      els.clear.addEventListener('click', function () {
        qtyInputs.forEach(function (i) { i.value = 0; });
        if (els.search) { els.search.value = ''; els.search.dispatchEvent(new Event('input')); }
        compute();
      });
    }

    /* -------- Export (placeholder: printable invoice) -------- */
    if (els.export) {
      els.export.addEventListener('click', function () {
        if (!validateCustomer()) return;
        var lines = [];
        qtyInputs.forEach(function (input) {
          var qty = parseInt(input.value, 10) || 0;
          if (qty <= 0) return;
          var row = input.closest('.sob__row');
          lines.push({
            shade: row.getAttribute('data-shade') || '',
            sku: row.querySelector('.sob__row-sku').textContent,
            len: row.getAttribute('data-len') || '',
            price: parseFloat(input.getAttribute('data-price')) || 0,
            qty: qty
          });
        });
        if (!lines.length) { alert('Add quantities before exporting.'); return; }
        exportPdf(lines);
      });
    }

    /* ---- Real PDF export (jsPDF + autotable, lazy-loaded) ---- */
    var pdfLibsPromise = null;
    function loadScript(src) {
      return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = resolve; s.onerror = function () { reject(new Error('load ' + src)); };
        document.head.appendChild(s);
      });
    }
    function ensurePdfLibs() {
      if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve();
      if (!pdfLibsPromise) {
        pdfLibsPromise = loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js')
          .then(function () { return loadScript('https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js'); });
      }
      return pdfLibsPromise;
    }

    // Load a (Shopify CDN, CORS-enabled) image URL into a PNG data URL for jsPDF.
    function loadImageData(url) {
      return new Promise(function (resolve) {
        if (!url) { resolve(null); return; }
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          try {
            var c = document.createElement('canvas');
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            resolve({ dataUrl: c.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
          } catch (e) { resolve(null); }
        };
        img.onerror = function () { resolve(null); };
        img.src = url;
      });
    }

    function exportPdf(lines) {
      var btn = els.export;
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Preparing PDF…';

      ensurePdfLibs().then(function () {
        return loadImageData(data.logo);
      }).then(function (logo) {
        var jsPDF = window.jspdf.jsPDF;
        var doc = new jsPDF({ unit: 'pt', format: 'a4' });

        // C&M brand palette (matches the on-page CSS variables)
        var INK = [18, 18, 18], MUTED = [111, 106, 99], LINE = [221, 215, 202], GOOD = [47, 125, 84], NEG = [176, 60, 45];
        var LM = 40, RM = 555;

        var subtotal = 0;
        var body = lines.map(function (l) {
          var lineTotal = l.qty * l.price;
          subtotal += lineTotal;
          return [l.shade, l.sku, l.len, String(l.qty), money(l.price), money(lineTotal)];
        });
        var current = null;
        volumeTiers.forEach(function (t) { if (subtotal >= t.min) current = t; });
        var pct = current ? current.pct : 0;
        var discount = Math.round(subtotal * pct / 100);
        var total = subtotal - discount;

        var cust = getCustomer();
        var enrolled = !!(els.exclusivity && els.exclusivity.checked);
        var introTier = isNewCustomer() ? highestUnlockedTier(collectOrderedItems()) : null;

        function drawExclusivityBadge(x, baseY) {
          var label = 'Exclusivity Program';
          doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
          var w = doc.getTextWidth(label) + 12;
          doc.setFillColor(GOOD[0], GOOD[1], GOOD[2]);
          doc.roundedRect(x, baseY - 8.5, w, 13, 6.5, 6.5, 'F');
          doc.setTextColor(255, 255, 255);
          doc.text(label, x + 6, baseY + 1);
          doc.setFont('helvetica', 'normal'); doc.setTextColor(INK[0], INK[1], INK[2]); doc.setFontSize(10);
        }

        // ---- Header ----
        if (logo && logo.dataUrl) {
          var lh = 30, lw = logo.w * (lh / logo.h);
          if (lw > 200) { lw = 200; lh = logo.h * (lw / logo.w); }
          doc.addImage(logo.dataUrl, 'PNG', LM, 30, lw, lh);
        } else {
          doc.setFont('times', 'italic'); doc.setFontSize(20); doc.setTextColor(INK[0], INK[1], INK[2]);
          doc.text('Covet & Mane', LM, 52);
        }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text('SALON ORDER', RM, 38, { align: 'right' });
        doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
        try { doc.text(new Date().toLocaleDateString('en-US'), RM, 52, { align: 'right' }); } catch (e) {}
        doc.setDrawColor(LINE[0], LINE[1], LINE[2]); doc.setLineWidth(0.5);
        doc.line(LM, 74, RM, 74);

        // ---- Prepared for ----
        var cy = 98;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        doc.text('PREPARED FOR', LM, cy); cy += 16;
        var fullName = (cust.firstName + ' ' + cust.lastName).trim();
        if (fullName) { doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(INK[0], INK[1], INK[2]); doc.text(fullName, LM, cy); cy += 15; }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(INK[0], INK[1], INK[2]);
        if (cust.company) {
          doc.text(cust.company, LM, cy);
          if (enrolled) drawExclusivityBadge(LM + doc.getTextWidth(cust.company) + 10, cy);
          cy += 14;
        } else if (enrolled) { drawExclusivityBadge(LM, cy); cy += 16; }
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
        if (cust.email) { doc.text(cust.email, LM, cy); cy += 13; }
        if (cust.address) { doc.splitTextToSize(cust.address, 320).forEach(function (a) { doc.text(a, LM, cy); cy += 13; }); }
        doc.text('New customer: ' + (cust.newCustomer ? 'Yes' : 'No'), LM, cy); cy += 22;

        // ---- Intro offer highlight bar (ties to gifts footnote via *) ----
        if (introTier) {
          doc.setFillColor(238, 244, 236);
          doc.roundedRect(LM, cy - 14, RM - LM, 23, 3, 3, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(GOOD[0], GOOD[1], GOOD[2]);
          doc.text('Introductory offer unlocked: ' + introTier.name + '  *', LM + 10, cy + 1);
          cy += 26;
        }

        // ---- Line items ----
        doc.autoTable({
          startY: cy,
          margin: { left: LM, right: 40 },
          head: [['Shade', 'SKU', 'Length', 'Qty', 'Price', 'Total']],
          body: body,
          theme: 'plain',
          styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 6, bottom: 6, left: 8, right: 8 }, textColor: INK, valign: 'middle' },
          headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
          alternateRowStyles: { fillColor: [250, 247, 240] },
          columnStyles: {
            0: { cellWidth: 165 },
            1: { cellWidth: 162, fontSize: 8, textColor: MUTED },
            2: { cellWidth: 44 },
            3: { cellWidth: 30, halign: 'right' },
            4: { cellWidth: 52, halign: 'right' },
            5: { cellWidth: 62, halign: 'right' }
          }
        });

        // ---- Totals (right-aligned block) ----
        var ty = doc.lastAutoTable.finalY;
        doc.setDrawColor(LINE[0], LINE[1], LINE[2]); doc.setLineWidth(0.6);
        doc.line(LM, ty, RM, ty);
        ty += 22;
        var labelX = 458;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
        doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]); doc.text('Subtotal', labelX, ty, { align: 'right' });
        doc.setTextColor(INK[0], INK[1], INK[2]); doc.text(money(subtotal), RM, ty, { align: 'right' }); ty += 16;
        if (pct > 0) {
          doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]); doc.text('Volume discount (' + pct + '%)', labelX, ty, { align: 'right' });
          doc.setTextColor(NEG[0], NEG[1], NEG[2]); doc.text('-' + money(discount), RM, ty, { align: 'right' }); ty += 16;
        }
        ty += 4;
        doc.setDrawColor(LINE[0], LINE[1], LINE[2]); doc.setLineWidth(0.5); doc.line(360, ty, RM, ty); ty += 19;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text('Order total', labelX, ty, { align: 'right' });
        doc.text(money(total), RM, ty, { align: 'right' });

        // ---- Complimentary gifts footnote (bottom-left, tied to the * above) ----
        var y = ty + 36;
        function ensureSpace(h) { if (y + h > 800) { doc.addPage(); y = 48; } }
        if (introTier && introTier.gifts && introTier.gifts.length) {
          ensureSpace(34);
          doc.setDrawColor(LINE[0], LINE[1], LINE[2]); doc.setLineWidth(0.5); doc.line(LM, y - 15, 300, y - 15);
          doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
          doc.text('* Complimentary gifts included with ' + introTier.name, LM, y); y += 15;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90, 90, 90);
          var gv = 0;
          introTier.gifts.forEach(function (g) {
            gv += g.value || 0;
            ensureSpace(13);
            doc.text('•  ' + g.label, LM + 4, y);
            doc.text(money(g.value), 300, y, { align: 'right' });
            y += 13;
          });
          ensureSpace(18);
          doc.setDrawColor(LINE[0], LINE[1], LINE[2]); doc.setLineWidth(0.4); doc.line(LM + 4, y - 2, 300, y - 2);
          doc.setFont('helvetica', 'bold'); doc.setTextColor(INK[0], INK[1], INK[2]);
          doc.text('Complimentary value', LM + 4, y + 8);
          doc.text(money(gv), 300, y + 8, { align: 'right' }); y += 20;
        }

        doc.save('covet-mane-salon-order.pdf');
      }).catch(function () {
        // Fallback: printable window (browser Save as PDF)
        printInvoice(lines);
      }).then(function () {
        btn.disabled = false;
        btn.textContent = original;
      });
    }

    /* -------- Add to cart (placeholder) -------- */
    if (els.addcart) {
      els.addcart.addEventListener('click', function () {
        alert('Add to cart / draft-order generation will be wired up later (pending Shopify draft-order support).');
      });
    }

    function printInvoice(lines) {
      var subtotal = 0;
      var rowsHtml = lines.map(function (l) {
        var lineTotal = l.qty * l.price;
        subtotal += lineTotal;
        return '<tr><td>' + esc(l.shade) + '</td><td>' + esc(l.sku) + '</td><td>' + esc(l.len) + '</td><td style="text-align:right">' + l.qty + '</td><td style="text-align:right">' + money(l.price) + '</td><td style="text-align:right">' + money(lineTotal) + '</td></tr>';
      }).join('');
      var current = null;
      volumeTiers.forEach(function (t) { if (subtotal >= t.min) current = t; });
      var pct = current ? current.pct : 0;
      var discount = Math.round(subtotal * pct / 100);
      var total = subtotal - discount;

      var cust = getCustomer();
      var custName = (cust.firstName + ' ' + cust.lastName).trim();
      var enrolled = !!(els.exclusivity && els.exclusivity.checked);
      var badge = enrolled ? ' <span style="display:inline-block;background:#2f7d54;color:#fff;font-size:10px;font-weight:bold;letter-spacing:.04em;padding:2px 7px;border-radius:3px;vertical-align:middle">Exclusivity Program</span>' : '';
      var custHtml = '<div style="margin-top:20px;font-size:12px;line-height:1.6">' +
        '<div style="letter-spacing:.14em;text-transform:uppercase;font-size:10px;color:#787878">Prepared for</div>' +
        (custName ? '<div style="font-weight:bold;font-size:15px">' + esc(custName) + '</div>' : '') +
        (cust.company ? '<div>' + esc(cust.company) + badge + '</div>' : (enrolled ? '<div>' + badge + '</div>' : '')) +
        (cust.email ? '<div>' + esc(cust.email) + '</div>' : '') +
        (cust.address ? '<div>' + esc(cust.address) + '</div>' : '') +
        '<div>New customer: ' + (cust.newCustomer ? 'Yes' : 'No') + '</div>' +
      '</div>';

      // Introductory offer: unlocked line at the top (*), complimentary gifts as the bottom footnote.
      var introTopHtml = '', giftsFootnoteHtml = '';
      if (cust.newCustomer) {
        var tier = highestUnlockedTier(collectOrderedItems());
        if (tier) {
          introTopHtml = '<div style="margin-top:16px;font-size:13px;font-weight:bold">Introductory offer unlocked: ' + esc(tier.name) + ' *</div>';
          if (tier.gifts && tier.gifts.length) {
            var giftsHtml = '', gv = 0;
            tier.gifts.forEach(function (g) {
              gv += g.value || 0;
              giftsHtml += '<div style="display:flex;justify-content:space-between;color:#555;max-width:360px"><span>•&nbsp;' + esc(g.label) + '</span><span>' + money(g.value) + '</span></div>';
            });
            giftsFootnoteHtml = '<div style="margin-top:20px;font-size:12px">' +
              '<div style="font-style:italic;color:#787878">* Complimentary gifts included with ' + esc(tier.name) + ':</div>' +
              giftsHtml +
              '<div style="font-weight:bold;margin-top:6px">Complimentary value: ' + money(gv) + '</div>' +
            '</div>';
          }
        }
      }

      var w = window.open('', '_blank');
      if (!w) { alert('Please allow pop-ups to export the order.'); return; }
      w.document.write(
        '<html><head><title>Covet & Mane — Salon Order</title><style>' +
        'body{font-family:Georgia,serif;color:#1c1a17;padding:40px;}h1{font-style:italic;letter-spacing:.1em;text-transform:uppercase;font-size:24px;margin:0}' +
        'table{width:100%;border-collapse:collapse;margin-top:24px;font-size:13px}th,td{padding:8px;border-bottom:1px solid #ddd;text-align:left}' +
        'tfoot td{border:0;padding:4px 8px}.tot{font-size:18px;font-weight:bold}' +
        '</style></head><body>' +
        (data.logo ? '<img src="' + esc(data.logo) + '" alt="" style="max-width:240px;height:auto;display:block">' : '<h1>Covet &amp; Mane</h1>') +
        '<p style="letter-spacing:.14em;text-transform:uppercase;font-size:11px">Salon Order</p>' +
        custHtml +
        introTopHtml +
        '<table><thead><tr><th>Shade</th><th>SKU</th><th>Length</th><th style="text-align:right">Qty</th><th style="text-align:right">Price</th><th style="text-align:right">Total</th></tr></thead>' +
        '<tbody>' + rowsHtml + '</tbody>' +
        '<tfoot>' +
          '<tr><td colspan="5" style="text-align:right">Subtotal</td><td style="text-align:right">' + money(subtotal) + '</td></tr>' +
          (pct > 0 ? '<tr><td colspan="5" style="text-align:right">Volume discount (' + pct + '%)</td><td style="text-align:right">−' + money(discount) + '</td></tr>' : '') +
          '<tr class="tot"><td colspan="5" style="text-align:right">Grand total</td><td style="text-align:right">' + money(total) + '</td></tr>' +
        '</tfoot></table>' +
        giftsFootnoteHtml +
        '<script>window.onload=function(){window.print();}<\/script>' +
        '</body></html>'
      );
      w.document.close();
    }

    function esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    applyNewCustomer();
    updateClip();
  }

  function boot() { document.querySelectorAll('[data-sob-root]').forEach(initRoot); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-sob-root]');
    if (root) initRoot(root);
  });
})();